
function isNullish(value) {
    return value === null || value === undefined || value === '';
}

if (!customElements.get('gift-dispatcher')) {
    customElements.define(
        'gift-dispatcher',
        class GiftDispatcher extends HTMLElement {
            constructor() {
                super();
                this.gifts = this.querySelectorAll('input[type="hidden"]');
                this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer') || document.querySelector('deployer-cart-drawer');
                this.cartURLs = {
                    add: `/cart/add.js`,
                    change: `/cart/change.js`,
                    update: `/cart/update.js`,
                    cart: `/cart.json`,
                };
                this.limitedVariantIds = [];
                this.maxQuantity = 1;
                
                // Système de verrouillage pour éviter les race conditions
                this.isProcessing = false;
                this.pendingValidation = false;
                this.validationDebounceTimer = null;

                this.activateFetchInterception();
            }

            connectedCallback() {
                if (window.deployerAdapter.enable_gift_dispatcher === true) {
                    this.initializeLimitedVariantIds();
                    this.listenEvents();
                }
            }

            disconnectedCallback() {
                this.removeEvents();
                if (this.validationDebounceTimer) {
                    clearTimeout(this.validationDebounceTimer);
                }
            }

            initializeLimitedVariantIds() {
                this.gifts.forEach(gift => {
                    if (!isNullish(gift.dataset.variantId)) {
                        this.limitedVariantIds.push(parseInt(gift.dataset.variantId));
                    }
                });
            }

            activateFetchInterception() {
                const originalFetch = window.fetch;
                const instance = this;

                window.fetch = async function (input, init) {
                    try {
                        const url = typeof input === 'string' ? input : input.url;
                        const method = init?.method || 'GET';

                        if (
                            url.includes('/cart/add.js') ||
                            url.includes('/cart/change.js') ||
                            url.includes('/cart/update.js')
                        ) {
                            const body = init?.body ? JSON.parse(init.body) : null;
                            if (body?.items) {
                                for (const item of body.items) {
                                    if (instance.limitedVariantIds.includes(item.id)) {
                                        if (url.includes('/cart/add.js') && item.quantity > instance.maxQuantity) {
                                            console.warn(
                                                `Tentative d'ajouter plus de ${instance.maxQuantity} unités du produit ${item.id}. Quantité ajustée à 0.`
                                            );
                                            item.quantity = 0;
                                        } else if (item.quantity > instance.maxQuantity) {
                                            console.warn(
                                                `Tentative de modification à plus de ${instance.maxQuantity} unités du produit ${item.id}. Quantité limitée à ${instance.maxQuantity}.`
                                            );
                                            item.quantity = instance.maxQuantity;
                                        }
                                    }
                                }
                            }
                            if (body?.id && instance.limitedVariantIds.includes(body.id)) {
                                if (url.includes('/cart/add.js') && body.quantity > instance.maxQuantity) {
                                    console.warn(
                                        `Tentative d'ajouter plus de ${instance.maxQuantity} unités du produit ${body.id}. Quantité ajustée à 0.`
                                    );
                                    body.quantity = 0;
                                } else if (body.quantity > instance.maxQuantity) {
                                    console.warn(
                                        `Tentative de modification à plus de ${instance.maxQuantity} unités du produit ${body.id}. Quantité limitée à ${instance.maxQuantity}.`
                                    );
                                    body.quantity = instance.maxQuantity;
                                }
                            }
                            init.body = JSON.stringify(body);
                        }
                        return originalFetch(input, init);
                    } catch (error) {
                        console.error('Erreur dans l\'interception de fetch:', error);
                        return originalFetch(input, init);
                    }
                };
            }

            listenEvents() {
                // Utiliser une fonction debounced pour éviter les validations multiples
                const debouncedValidation = () => {
                    if (this.validationDebounceTimer) {
                        clearTimeout(this.validationDebounceTimer);
                    }
                    
                    this.validationDebounceTimer = setTimeout(() => {
                        this.scheduleValidation();
                    }, 300);
                };

                document.addEventListener('deployer-cart-drawer:refresh', debouncedValidation);
                document.addEventListener('cart-product:add', debouncedValidation);
                document.addEventListener('cart-product:change', debouncedValidation);

                const cartRemoveElements = document.querySelectorAll('.cart__remove');
                cartRemoveElements.forEach(element => {
                    element.addEventListener('click', event => {
                        event.preventDefault();
                        setTimeout(debouncedValidation, 500);
                    });
                });
            }

            // Planifier une validation (avec système de verrouillage)
            async scheduleValidation() {
                if (this.isProcessing) {
                    this.pendingValidation = true;
                    return;
                }

                this.isProcessing = true;
                this.pendingValidation = false;

                try {
                    const cart = await this.getCart();
                    if (cart) {
                        await this.validateConditions(cart);
                    }
                } catch (error) {
                    console.error('Erreur lors de la validation des conditions:', error);
                } finally {
                    this.isProcessing = false;
                    
                    // Si une validation était en attente, la relancer
                    if (this.pendingValidation) {
                        setTimeout(() => this.scheduleValidation(), 100);
                    }
                }
            }

            removeEvents() { }

            async getCart() {
                return fetch(this.cartURLs.cart, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json'
                    }
                })
                    .then(response => response.json())
                    .then(response => {
                        return response;
                    })
                    .catch((error) => {
                        console.error('Error:', error);
                    });
            }

            async validateConditions(cart) {
                // Grouper les gifts par variant ID pour gérer les conflits A/B test
                const giftsByVariant = this.groupGiftsByVariant();
                
                for (const [variantId, gifts] of giftsByVariant) {
                    // Déterminer quel gift doit être traité en priorité selon l'A/B test
                    const priorityGift = this.getPriorityGift(gifts);
                    
                    if (!priorityGift) {
                        // Aucun gift valide pour ce variant, retirer le produit s'il est dans le panier
                        if (this.isAlreadyOnCart(cart, variantId)) {
                            const restrictedItem = cart.items.find(item => item.variant_id == variantId);
                            await this.removeGift(restrictedItem?.key, variantId);
                            cart = await this.getCart();
                        }
                        continue;
                    }

                    const tierType = priorityGift.dataset.tierType;
                    const isSufficient = this.isTierSatisfied(cart, tierType, priorityGift);

                    const restrictedItem = cart.items.find(item => item.variant_id == variantId);
                    
                    // Si le produit est déjà dans le panier avec quantité > 1, limiter à 1
                    if (restrictedItem && restrictedItem.quantity > this.maxQuantity) {
                        await this.limitProductQuantity(restrictedItem.key, this.maxQuantity);
                        cart = await this.getCart();
                        continue;
                    }

                    // Si les conditions ne sont plus remplies, retirer le cadeau
                    if (isSufficient === false) {
                        if (this.isAlreadyOnCart(cart, variantId)) {
                            await this.removeGift(restrictedItem?.key, variantId);
                            cart = await this.getCart();
                        }
                        continue;
                    }

                    // Si les conditions sont remplies et que le produit n'est pas déjà dans le panier
                    if (!this.isAlreadyOnCart(cart, variantId)) {
                        await this.addToCartGift(variantId);
                        cart = await this.getCart();
                    }
                }
            }

            groupGiftsByVariant() {
                const giftsByVariant = new Map();
                
                this.gifts.forEach(gift => {
                    if (isNullish(gift.dataset.variantId)) return;
                    
                    const variantId = parseInt(gift.dataset.variantId);
                    if (!giftsByVariant.has(variantId)) {
                        giftsByVariant.set(variantId, []);
                    }
                    giftsByVariant.get(variantId).push(gift);
                });
                
                return giftsByVariant;
            }

            getPriorityGift(gifts) {
                // Filtrer d'abord les gifts valides selon la logique A/B test
                const validGifts = gifts.filter(gift => this.isABTestValid(gift));
                
                if (validGifts.length === 0) {
                    return null; // Aucun gift valide
                }
                
                if (validGifts.length === 1) {
                    return validGifts[0]; // Un seul gift valide
                }
                
                // Si plusieurs gifts valides, prioriser par identificateur
                // L'ordre de priorité suit l'ordre alphabétique des variantes
                const priorityOrder = ['testingB', 'testingA'];
                
                const sortedGifts = validGifts.sort((a, b) => {
                    const aTesting = a.dataset.testing || '';
                    const bTesting = b.dataset.testing || '';
                    
                    const aPriority = priorityOrder.indexOf(aTesting);
                    const bPriority = priorityOrder.indexOf(bTesting);
                    
                    // Les gifts sans identificateur de test ont la priorité la plus basse
                    if (aPriority === -1 && bPriority === -1) return 0;
                    if (aPriority === -1) return 1;
                    if (bPriority === -1) return -1;
                    
                    return aPriority - bPriority;
                });
                
                return sortedGifts[0];
            }

            isABTestValid(gift) {
                // Si ce n'est pas un produit testé, on l'ajoute normalement
                if (isNullish(gift.dataset.testing) || gift.dataset.testing === 'false') {
                    return true;
                }

                const testingIdentifier = gift.dataset.testing;
                const activeVariant = window.giftTestingVariant;

                // Si aucune variante n'est définie, ne pas afficher les produits en test
                if (isNullish(activeVariant)) {
                    return false;
                }

                // Le gift est valide si son identificateur correspond à la variante active
                // Exemple : si activeVariant = 'C', seuls les gifts avec testing='testingC' sont affichés
                const expectedIdentifier = `testing${activeVariant.toUpperCase()}`;
                
                return testingIdentifier === expectedIdentifier;
            }

            async limitProductQuantity(itemKey, maxQuantity) {
                if (!itemKey) {
                    console.error('limitProductQuantity: itemKey est requis');
                    return;
                }

                const bodyConfig = JSON.stringify({
                    updates: {
                        [itemKey]: maxQuantity
                    }
                });

                try {
                    const response = await fetch(this.cartURLs.update, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: bodyConfig,
                    });
                    const result = await response.json();
                    return result;
                } catch (error) {
                    console.error('Erreur lors de la mise à jour de la quantité:', error);
                    throw error;
                }
            }

            isTierSatisfied(cart, type, gift) {
                switch (type) {
                    case 'cart_items_count':
                        return GiftDispatcherOnCartItemsCount.validateConditions(cart, gift);
                    case 'cart_amount':
                        return GiftDispatcherOnCartAmount.validateConditions(cart, gift);
                    case 'product_on_cart':
                        return GiftDispatcherOnProductInCart.validateConditions(cart, gift);
                    default:
                        return false;
                }
            }

            isAlreadyOnCart(cart, variantId) {
                return cart.items.some((item) => item.variant_id == variantId);
            }

            updateCart() {
                // Ne pas déclencher deployer-cart-drawer:refresh pour éviter la boucle infinie
                // On rafraîchit seulement le drawer sans re-valider les conditions
                const cartDrawer = document.querySelector('deployer-cart-drawer');
                if (cartDrawer && typeof cartDrawer.refreshCart === 'function') {
                    cartDrawer.refreshCart();
                }
            }

            async addToCartGift(variantId) {
                const bodyConfig = JSON.stringify({
                    items: [
                        {
                            id: variantId,
                            quantity: 1,
                        },
                    ],
                });

                try {
                    const response = await fetch(this.cartURLs.add, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: bodyConfig,
                    });
                    const result = await response.json();
                    
                    if (result.status && result.status !== 200) {
                        console.error('Erreur lors de l\'ajout du cadeau:', result.description || result.message);
                        return null;
                    }
                    
                    this.updateCart();
                    return result;
                } catch (error) {
                    console.error('Erreur lors de l\'ajout du cadeau au panier:', error);
                    throw error;
                }
            }

            async removeGift(itemKey, variantId) {
                if (!itemKey) {
                    console.error('removeGift: itemKey est requis');
                    return;
                }

                const bodyConfig = JSON.stringify({
                    updates: {
                        [itemKey]: 0
                    }
                });

                try {
                    const response = await fetch(this.cartURLs.update, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: bodyConfig,
                    });
                    const result = await response.json();
                    this.updateCart();
                    return result;
                } catch (error) {
                    console.error('Erreur lors de la suppression du cadeau:', error);
                    throw error;
                }
            }
        }
    )
}


var GiftDispatcherOnCartAmount = class {
    static isCartAmountSufficient(cart, cartAmount) {
        const cartTotal = cart.items_subtotal_price;
        return cartTotal >= cartAmount;
    }

    static validateConditions(cart, gift) {
        if (isNullish(gift.dataset.cartAmount)) return false;

        const cartAmount = parseFloat(gift.dataset.cartAmount);
        const isSufficient = this.isCartAmountSufficient(cart, cartAmount);
        const isAvailable = gift.dataset.variantAvailable === "true";

        if (!isSufficient) {
            // console.warn(`Tier is not sufficient for adding gift ${variantId}`);
            return false;
        }

        if (!isAvailable) {
            // console.warn(`Gift ${variantId} is not available`);
            return false;
        }

        return true;
    }
}

var GiftDispatcherOnCartItemsCount = class {
    static isCartItemsCountSufficient(cart, cartItemsCount) {
        const cartItems = cart.item_count;
        return cartItems >= cartItemsCount;
    }

    static validateConditions(cart, gift) {
        if (isNullish(gift.dataset.cartItemsCount)) return false;

        const cartItemsCount = parseInt(gift.dataset.cartItemsCount);
        const isSufficient = this.isCartItemsCountSufficient(cart, cartItemsCount);
        const isAvailable = gift.dataset.variantAvailable === "true";

        if (!isSufficient) {
            // console.warn(`Tier is not sufficient for adding gift ${variantId}`);
            return false;
        }

        if (!isAvailable) {
            // console.warn(`Gift ${variantId} is not available`);
            return false;
        }

        return true;
    }
}

var GiftDispatcherOnProductInCart = class {
    static isProductDispatcherOnCart(cart, productId) {
        return cart.items.some((item) => item.product_id == productId);
    }

    static validateConditions(cart, gift) {
        if (isNullish(gift.dataset.variantId)) {
            // console.warn("Variant ID is not defined");
            return false;
        }

        const isAvailable = gift.dataset.variantAvailable === "true";
        const productDispatcherId = parseInt(gift.dataset.productDispatcherId);
        const isProductDispatcherOnCart = this.isProductDispatcherOnCart(cart, productDispatcherId);

        if (!isProductDispatcherOnCart) {
            // console.warn(`Dispatcher is not on cart`);
            return false;
        }

        if (!isAvailable) {
            // console.warn(`Gift ${variantId} is not available`);
            return false;
        }

        return true;
    }
}